import { useEffect, useRef, useState, type ReactNode } from "react";
import { buzz } from "../lib/haptics";

// A card that follows your thumb and flies away when you let go past a
// threshold.
//
// This was written inside the finale and lived there; it is out here now because
// the same gesture is the reveal deck, the check-in approve step and the finale
// stack, and three copies of pointer maths is three chances to get the fast
// flick wrong.
//
// The subtle part, kept from the original: the ref is the truth and the state is
// only for painting. Reading the release decision off state made it depend on a
// re-render landing between the last move and the release, so a fast flick could
// be judged as no movement at all and snap back instead of advancing.

export interface SwipeCardProps {
  children: ReactNode;
  onSwipe: (direction: 1 | -1) => void;
  // Which directions actually do something. The finale takes either; a yes/no
  // choice wants both to mean different things; a one-way deck takes one.
  allow?: "both" | "left" | "right";
  threshold?: number;
  className?: string;
  // The labels for each direction. They are drawn on the card as arrow cues, so
  // the gesture is visible before anybody tries it.
  hintLeft?: string;
  hintRight?: string;
  // A yes/no deck. Dragging right grows a green tick, left a red cross, both
  // scaling with the drag, so you can see what you are about to commit to
  // before you let go. Off by default: a deck where both directions mean "next"
  // must not be coloured like a verdict.
  decide?: boolean;
  // What the card says as it leaves. Defaults to the direction's label.
  doneLeft?: string;
  doneRight?: string;
  // Commit from outside, so a button does exactly what the swipe does, with the
  // same animation and the same confirmation. Set it to a direction to fire.
  commit?: 1 | -1 | null;
  // A single small lean on the very first card, so it reads as something you
  // can pick up rather than a page you scroll. Once per visit, never again.
  nudge?: boolean;
}

export function SwipeCard({
  children, onSwipe, allow = "both", threshold = 90, className = "", hintLeft, hintRight,
  decide = false, doneLeft, doneRight, commit = null, nudge = false,
}: SwipeCardProps) {
  const [dx, setDx] = useState(0);
  const [flungTo, setFlungTo] = useState<number | null>(null);
  // Two of these on purpose. The REF decides; the state only paints.
  //
  // Extracting this component, I turned dragging into state because the
  // settling class needs to re-render. That put the release decision behind a
  // state update, which is the exact race the original code was written to
  // avoid: a quick flick can release before React has re-rendered, the handler
  // still sees dragging=false, and the card snaps back instead of advancing.
  // Observed exactly that: the card tracked the thumb and then refused to
  // commit. The ref is read for decisions, the state only for the class.
  const draggingRef = useRef(false);
  const [draggingPaint, setDraggingPaint] = useState(false);
  // A press is only a drag once it has clearly gone sideways. Until then it is
  // "maybe": a tap on a button, or a vertical scroll of the text inside the card.
  // Capturing the pointer on the first touch took both of those away, so buttons
  // inside a card could lose their click and a thumb scrolling a long question
  // dragged the card instead.
  const pendingRef = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const dxRef = useRef(0);
  const passed = useRef(false);

  const permitted = (dir: 1 | -1) =>
    allow === "both" || (allow === "right" && dir === 1) || (allow === "left" && dir === -1);

  function down(e: React.PointerEvent) {
    if (flungTo !== null) return;
    // Controls inside the card are pressed, never dragged.
    if ((e.target as HTMLElement).closest("button, a, input, textarea, select, [data-no-drag]")) return;
    pendingRef.current = true;
    startX.current = e.clientX;
    startY.current = e.clientY;
    dxRef.current = 0;
    passed.current = false;
  }

  function move(e: React.PointerEvent) {
    if (pendingRef.current) {
      const x = e.clientX - startX.current;
      const y = e.clientY - startY.current;
      if (Math.abs(x) < 6 && Math.abs(y) < 6) return;
      pendingRef.current = false;
      // Mostly vertical: leave it to the scroll area, the card stays put.
      if (Math.abs(y) > Math.abs(x)) return;
      draggingRef.current = true;
      setDraggingPaint(true);
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    }
    if (!draggingRef.current) return;
    const travelled = e.clientX - startX.current;
    dxRef.current = travelled;
    setDx(travelled);
    // One tick the moment it would commit, so the threshold is felt rather than
    // guessed at.
    const over = Math.abs(travelled) > threshold && permitted(travelled > 0 ? 1 : -1);
    if (over && !passed.current) { passed.current = true; buzz("tick"); }
    if (!over) passed.current = false;
  }

  function up() {
    pendingRef.current = false;
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDraggingPaint(false);
    const travelled = dxRef.current;
    dxRef.current = 0;
    const dir: 1 | -1 = travelled > 0 ? 1 : -1;
    if (Math.abs(travelled) > threshold && permitted(dir)) fling(dir);
    else setDx(0);
  }

  function fling(dir: 1 | -1) {
    if (flungTo !== null) return;
    buzz("commit");
    setFlungTo(dir);
    // Long enough to read what just happened, short enough not to be a wait.
    window.setTimeout(() => onSwipe(dir), decide ? 380 : 220);
  }

  // A button press is the same commit as a swipe, animation and all.
  useEffect(() => {
    if (commit === 1 || commit === -1) fling(commit);
    // fling is stable enough here: it only guards on flungTo, which it sets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit]);

  const offset = flungTo !== null ? flungTo * 700 : dx;

  // How far through the gesture, for the tint and the tick growing with it.
  const towards: 1 | -1 = dx > 0 ? 1 : -1;
  const judging = decide && Math.abs(dx) > 6 && permitted(towards) ? towards : 0;
  const progress = Math.min(1, Math.abs(dx) / threshold);
  const stamp = flungTo === null ? null : (flungTo === 1 ? (doneRight ?? hintRight) : (doneLeft ?? hintLeft));

  return (
    <div className="swipe-wrap">
      <div
        className={`swipe-card ${className} ${decide ? "deciding" : ""} ${nudge && dx === 0 && flungTo === null ? "nudge" : ""} ${flungTo !== null ? "flung" : ""} ${draggingPaint ? "" : "settling"}`}
        style={{
          transform: `translateX(${offset}px) rotate(${offset / 26}deg)`,
          // A deciding card keeps its face on the way out, because the whole
          // point of the last frame is reading what just happened.
          opacity: flungTo !== null && !decide ? 0 : 1,
        }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        {children}

        {/* The cues. Always there, so nobody has to discover the gesture by
            accident, and faint enough to stay out of the way of the content. */}
        {(hintLeft || hintRight) && (
          <div className="swipe-cues" aria-hidden="true">
            {hintLeft && (
              <span className={`swipe-cue left${judging === -1 ? " on" : ""}`}>
                <Chevron dir="left" /> {hintLeft}
              </span>
            )}
            {hintRight && (
              <span className={`swipe-cue right${judging === 1 ? " on" : ""}`}>
                {hintRight} <Chevron dir="right" />
              </span>
            )}
          </div>
        )}

        {/* Which way this is going, growing as it gets closer to committing. */}
        {judging !== 0 && flungTo === null && (
          <div
            className={`swipe-judge ${judging === 1 ? "yes" : "no"}`}
            style={{ opacity: 0.25 + progress * 0.75 }}
            aria-hidden="true"
          >
            <span className="swipe-judge-mark" style={{ transform: `scale(${0.7 + progress * 0.35})` }}>
              {judging === 1 ? "✓" : "✕"}
            </span>
          </div>
        )}

        {/* What just happened, on its way out. */}
        {decide && stamp && (
          <div className={`swipe-stamp ${flungTo === 1 ? "yes" : "no"}`} role="status">{stamp}</div>
        )}
      </div>
    </div>
  );
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={dir === "left" ? "m14 6-6 6 6 6" : "m10 6 6 6-6 6"} />
    </svg>
  );
}

// Expose the fling for a button, so a card is never gesture-only. Someone who
// cannot or would rather not swipe still has to be able to get through this.
export function useDeck<T>(items: T[]) {
  const [index, setIndex] = useState(0);
  return {
    index,
    current: items[index] as T | undefined,
    remaining: Math.max(0, items.length - index),
    done: index >= items.length,
    advance: () => setIndex((i) => i + 1),
    reset: () => setIndex(0),
  };
}
